import {
  Box,
  Stack,
  useBoolean,
  useFormControlContext,
} from '@chakra-ui/react';
import createDividerPlugin from '@draft-js-plugins/divider';
import Editor from '@draft-js-plugins/editor';
import createImagePlugin from '@draft-js-plugins/image';
import createVideoPlugin from '@draft-js-plugins/video';
import {
  DraftHandleValue,
  EditorState,
  KeyBindingUtil,
  RawDraftContentState,
  RichUtils,
  convertFromRaw,
  convertToRaw,
  getDefaultKeyBinding,
} from 'draft-js';
import 'draft-js/dist/Draft.css';
import debounce from 'lodash/debounce';
import * as React from 'react';

import { useHandlePastedFiles } from '@/hooks/useHandlePastedFiles';
import { Loading } from '@/imports/ui/chakra/components/Loading';
import { createLinkPlugin } from '@/modules/draft-js-link-plugin';

import { Toolbar } from './components/Toolbar';
import { VideoEditor } from './components/VideoEditor';

const linkPlugin = createLinkPlugin();
const videoPlugin = createVideoPlugin({
  videoComponent: VideoEditor,
});
const imagePlugin = createImagePlugin();
const dividerPlugin = createDividerPlugin();

/**
 *  like TextEditor, but used to save content state in the form rather
 *  than editor state
 *
 *  also optionally supports embedding images, videos, and dividers
 */
export const BlogEditor = React.forwardRef<
  Editor,
  {
    value: RawDraftContentState;
    onChange: (value: RawDraftContentState) => void;
    onBlur: () => void;
    placeholder: string;
    ariaLabel: string;
    hideToolbar?: boolean;
    onEditorFocus?: () => void;
    isDisabled?: boolean;
  }
>(
  (
    {
      value,
      onChange,
      onBlur,
      placeholder,
      ariaLabel,
      hideToolbar,
      onEditorFocus,
      isDisabled: isDisabledProp,
    },
    ref
  ) => {
    const { getUrlsFromFiles, isUploading } = useHandlePastedFiles();
    const formControlContext = useFormControlContext();
    const [isPasting, setIsPasting] = useBoolean(false);
    const [isFocused, setIsFocused] = useBoolean(false);

    const isLoading = isPasting || isUploading;
    const isDisabled =
      isDisabledProp || formControlContext?.isDisabled || isLoading;

    const [editorState, setEditorState] = React.useState(
      value
        ? EditorState.createWithContent(convertFromRaw(value))
        : EditorState.createEmpty()
    );

    const debouncedOnChange = React.useMemo(() => {
      return onChange ? debounce(onChange, 1000) : () => {};
    }, [onChange]);

    const onEditorStateChange = (editorState: EditorState) => {
      setEditorState(editorState);
      debouncedOnChange(convertToRaw(editorState.getCurrentContent()));
    };

    if (!value || !onChange) {
      return null;
    }

    const keyBindingFn = (event: React.KeyboardEvent<Element>) => {
      if (KeyBindingUtil.hasCommandModifier(event) && event.keyCode === 173) {
        return 'divider';
      }
      return getDefaultKeyBinding(event);
    };

    const handleKeyCommand = (command: string): DraftHandleValue => {
      switch (command) {
        case 'bold':
          onEditorStateChange(RichUtils.toggleInlineStyle(editorState, 'BOLD'));
          return 'handled';

        case 'italic':
          onEditorStateChange(
            RichUtils.toggleInlineStyle(editorState, 'ITALIC')
          );

          return 'handled';

        case 'underline':
          onEditorStateChange(
            RichUtils.toggleInlineStyle(editorState, 'UNDERLINE')
          );
          return 'handled';

        case 'divider':
          onEditorStateChange(dividerPlugin.addDivider(editorState));
          return 'handled';
      }

      return 'not-handled';
    };

    const handlePastedFiles = (files: File[]): DraftHandleValue => {
      setIsPasting.on();
      getUrlsFromFiles(files)
        .then((urls) =>
          urls.forEach((url) => {
            onEditorStateChange(imagePlugin.addImage(editorState, url, {}));
          })
        )
        .finally(() => setIsPasting.off());

      return 'handled';
    };

    return (
      <Stack
        flexGrow="1"
        display="flex"
        flexDirection="column"
        overflowY="hidden"
      >
        <Toolbar
          editorState={editorState}
          setEditorState={onEditorStateChange}
        />
        <Box
          overflowY="auto"
          w="full"
          flexGrow="1"
          sx={{
            '.public-DraftStyleDefault-block': {
              marginY: !hideToolbar ? '3' : '0',
              opacity: isDisabled ? 0.4 : undefined,
              fontWeight: 'normal',
            },
            '.public-DraftEditorPlaceholder-root, .public-DraftEditorPlaceholder-hasFocus':
              {
                color: 'gray.400',
              },
            h1: {
              fontSize: '30px',
              lineHeight: '36px',
              color: 'gray.700',
            },
            h2: {
              fontSize: '20px',
              lineHeight: '24px',
              color: 'gray.700',
            },
            h3: {
              fontSize: '18px',
              lineHeight: '26px',
              color: 'gray.700',
            },
            iframe: {
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'black',
            },
            blockquote: {
              color: 'gray.700',
              borderLeft: '3px solid',
              fontStyle: 'italic',
              paddingLeft: '16px',
              fontSize: '20px',
              lineHeight: '28px',
            },
          }}
          // Replicate Chakra <Input /> styling
          px="4"
          borderRadius="md"
          m={isFocused ? undefined : '1px'}
          border={isFocused ? '2px' : '1px'}
          borderColor={isFocused ? 'blue.500' : 'gray.200'}
        >
          <Editor
            ariaDescribedBy={undefined}
            ariaLabel={ariaLabel}
            ref={ref}
            keyBindingFn={keyBindingFn}
            handleKeyCommand={handleKeyCommand}
            onFocus={() => {
              setIsFocused.on();
              onEditorFocus && onEditorFocus();
            }}
            onBlur={() => {
              setIsFocused.off();
              onBlur();
            }}
            editorState={editorState}
            onChange={onEditorStateChange}
            spellCheck
            placeholder={placeholder}
            plugins={[videoPlugin, imagePlugin, dividerPlugin, linkPlugin]}
            readOnly={isDisabled}
            handlePastedFiles={handlePastedFiles}
          />

          {isLoading && <Loading />}
        </Box>
      </Stack>
    );
  }
);

BlogEditor.displayName = 'BlogEditor';
